"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import {
  resolveSignInTarget,
  signInUser,
} from "@/features/auth/api/auth-client";
import type { LoginScenario } from "@/features/auth/components/login/login-content";
import { authErrorMessage } from "@/features/auth/errors";
import { loginSchema, type LoginValues } from "@/features/auth/schemas";

/**
 * Behaviour for `/login`. Written as a hook during the four-way design bake-off
 * so all the candidates shared one model; kept as one now that variant B is the
 * page, since it keeps the form logic out of the layout.
 *
 * Same handshake as the `LoginForm` it replaced: post through NextAuth,
 * resolve the redirect client-side against the *current* origin (see
 * `resolveSignInTarget` — the session cookie doesn't travel to `NEXTAUTH_URL`'s
 * origin), and surface both URL-provided errors (NextAuth redirects back with
 * `?error=`) and inline submit failures.
 *
 * The field set is the live page's exactly: username, password, "Remember me",
 * one button and two links. Unlike `/admin/login` — whose reference has no
 * remember-me, hence its own local schema — this one reuses the shared
 * `loginSchema`, which carries `rememberMe` because this page has it.
 */

/** What `?scenario=populated|loading` fills in. Review data, not credentials. */
const PREFILL: LoginValues = {
  username: "ada_nwosu",
  password: "correcthorse",
  rememberMe: true,
};

const BLANK: LoginValues = { username: "", password: "", rememberMe: false };

/** `?scenario=error` — what a rejected credential check reports. Verbatim from
 * `authErrorMessage`, so the demo state can't drift from the real one. */
export const LOGIN_REJECTED = authErrorMessage("CredentialsSignin")!;

export interface LoginPageState {
  form: UseFormReturn<LoginValues>;
  /** Submitting — real, or forced by `?scenario=loading`. Locks the inputs. */
  submitting: boolean;
  /** The alert message to show, or null. */
  formError: string | null;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export function useLoginPage(scenario: LoginScenario): LoginPageState {
  const router = useRouter();
  const params = useSearchParams();
  const callbackUrl = params.get("callbackUrl") ?? "/";
  const [formError, setFormError] = useState<string | null>(
    authErrorMessage(params.get("error")),
  );

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues:
      scenario === "populated" || scenario === "loading" ? PREFILL : BLANK,
  });

  async function submit(values: LoginValues) {
    setFormError(null);
    const result = await signInUser(values, callbackUrl);
    if (result.ok) {
      router.push(resolveSignInTarget(result.url, callbackUrl));
      router.refresh();
      return;
    }
    setFormError(authErrorMessage(result.error) ?? "Sign in failed.");
  }

  return {
    form,
    submitting: scenario === "loading" || form.formState.isSubmitting,
    formError: scenario === "error" ? LOGIN_REJECTED : formError,
    onSubmit: form.handleSubmit(submit),
  };
}
